import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

export interface ProcessFormatItemData {
  itemname: string;
  noofbiscuits: string;
  qtyused: string;
  status: string;
  variance: string;
}

export interface ProcessFormatCycleData {
  cycleNum: string;
  product: string;
  executiveName: string;
  lineNo: string;
  tourstartdate: string;
  items: ProcessFormatItemData[];
}

interface OfflineSavedData {
  cycleNo: number;
  records: ProcessFormatCycleData[];
  timestamp: number;
}

interface ProcessFormatState {
  fetchedCycles: ProcessFormatCycleData[];
  offlineSavedData: OfflineSavedData[];
  isLoading: boolean;
  isSyncing: boolean;
  error: string | null;
  lastFetchTimestamp: number | null;
}

const initialState: ProcessFormatState = {
  fetchedCycles: [],
  offlineSavedData: [],
  isLoading: false,
  isSyncing: false,
  error: null,
  lastFetchTimestamp: null,
};

const processFormatSlice = createSlice({
  name: 'processFormat',
  initialState,
  reducers: {
    addOfflineData: (state, action: PayloadAction<OfflineSavedData>) => {
      state.offlineSavedData.push(action.payload);
    },
    clearOfflineData: (state) => {
      state.offlineSavedData = [];
    },
    removeOfflineDataItem: (state, action: PayloadAction<number>) => {
      state.offlineSavedData = state.offlineSavedData.filter(
        (item) => item.cycleNo !== action.payload
      );
    },
    setFetchedCycles: (state, action: PayloadAction<ProcessFormatCycleData[]>) => {
      state.fetchedCycles = action.payload;
      state.lastFetchTimestamp = Date.now();
    },
    clearError: (state) => {
      state.error = null;
    },
    resetState: (state) => {
      state.fetchedCycles = [];
      state.offlineSavedData = [];
      state.isLoading = false;
      state.isSyncing = false;
      state.error = null;
      state.lastFetchTimestamp = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setSyncing: (state, action: PayloadAction<boolean>) => {
      state.isSyncing = action.payload;
    },
  },
});

export const {
  addOfflineData,
  clearOfflineData,
  removeOfflineDataItem,
  setFetchedCycles,
  clearError,
  resetState,
  setLoading,
  setSyncing,
} = processFormatSlice.actions;

export const selectProcessFormatState = (state: { processFormat: ProcessFormatState }) =>
  state.processFormat;
export const selectOfflineDataCount = (state: { processFormat: ProcessFormatState }) =>
  state.processFormat.offlineSavedData.length;
export const selectFetchedCycles = (state: { processFormat: ProcessFormatState }) =>
  state.processFormat.fetchedCycles;

export default processFormatSlice.reducer;
